import { apiClient } from "./authService";
import { unwrapItem, unwrapList } from "../utils/apiResponse";

export type PackageScopeType = "CATEGORY" | "ALL";

export interface SessionPackage {
  id: string;
  name?: string;
  description?: string | null;
  price?: number | string;
  session_count?: number;
  validity_days?: number;
  scope_type?: PackageScopeType;
  category_id?: string | null;
  category?: { id: string; name?: string } | null;
  active?: boolean;
  [key: string]: any;
}

export interface CreatePackagePayload {
  name: string;
  description?: string;
  price: number;
  session_count: number;
  validity_days: number;
  scope_type: PackageScopeType;
  category_id?: string;
  active?: boolean;
}

export interface UpdatePackagePayload {
  name?: string;
  description?: string;
  price?: number;
  session_count?: number;
  validity_days?: number;
  active?: boolean;
}

export interface PurchasePackagePayload {
  client_id: string;
  business_id: string;
  unit_id?: string;
}

export type ClientPackagePaymentStatus =
  | "UNPAID"
  | "PARTIAL"
  | "PAID"
  | "CANCELLED"
  | "REFUNDED";

export interface ClientPackage {
  id: string;
  client_id?: string;
  package_id?: string;
  sessions_total?: number;
  sessions_remaining?: number;
  expires_at?: string;
  status?: string;
  /** Unlocks once PARTIAL/PAID — an UNPAID package cannot redeem a class booking yet. */
  payment_status?: ClientPackagePaymentStatus;
  invoice_id?: string | null;
  package?: SessionPackage | null;
  [key: string]: any;
}

export const createPackage = async (
  payload: CreatePackagePayload
): Promise<SessionPackage> => {
  const response = await apiClient.post("/packages", payload);
  return unwrapItem(response.data, "package") as SessionPackage;
};

export const getAllPackages = async (): Promise<SessionPackage[]> => {
  const response = await apiClient.get("/packages");
  return unwrapList(response.data, "packages") as SessionPackage[];
};

export const getPackageById = async (id: string): Promise<SessionPackage> => {
  const response = await apiClient.get(`/packages/${id}`);
  return unwrapItem(response.data, "package") as SessionPackage;
};

export const updatePackage = async (
  id: string,
  payload: UpdatePackagePayload
): Promise<SessionPackage> => {
  const response = await apiClient.patch(`/packages/${id}`, payload);
  return unwrapItem(response.data, "package") as SessionPackage;
};

/** Charges the client immediately and creates their redeemable quota. */
export const purchasePackage = async (
  id: string,
  payload: PurchasePackagePayload
): Promise<ClientPackage> => {
  const response = await apiClient.post(`/packages/${id}/purchase`, payload);
  return unwrapItem(response.data, "client_package") as ClientPackage;
};

/** A client's purchase history, most recent first. */
export const getClientPackages = async (
  clientId: string
): Promise<ClientPackage[]> => {
  const response = await apiClient.get(`/packages/clients/${clientId}`);
  return unwrapList(response.data, "client_packages", "packages") as ClientPackage[];
};

/**
 * Cancels a client's package purchase — refunds/cancels the underlying invoice
 * if it was paid, credits nothing back since remaining sessions are void.
 */
export const cancelClientPackage = async (
  clientPackageId: string
): Promise<ClientPackage> => {
  const response = await apiClient.post(
    `/packages/clients/${clientPackageId}/cancel`
  );
  return unwrapItem(response.data, "client_package") as ClientPackage;
};

export interface ClientPackageSummary {
  client_id: string;
  active_package_count: number;
  next_expiry: string | null;
  /**
   * Not returned by the API yet — /packages/clients-summary currently only
   * gives a count. Read defensively so the client-name display upgrades
   * automatically the day the backend adds it, without another frontend change.
   */
  active_packages?: { id: string; name: string }[];
}

/**
 * Per-client rollup for a client list view — how many currently-redeemable
 * packages (active, paid, sessions left, unexpired) each client holds, and
 * the soonest of those expiries. Packages aren't scoped to a business, so
 * this covers the whole org in one call; a client absent from the response
 * has zero redeemable packages.
 */
export const getClientPackagesSummary = async (
  clientIds?: string[]
): Promise<ClientPackageSummary[]> => {
  const response = await apiClient.get("/packages/clients-summary", {
    params:
      clientIds && clientIds.length > 0
        ? { clientIds: clientIds.join(",") }
        : undefined,
  });
  return unwrapList(response.data, "summary") as ClientPackageSummary[];
};
